import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRoute, Router, UrlTree } from '@angular/router';
import { NavigationInfo } from '@barthauer/abc/core';
import { AbcConfigService } from '@barthauer/abc/core/configuration';
import { from, iif, Observable, of } from 'rxjs';
import { catchError, concatMap, map } from 'rxjs/operators';
import { IProfileSettings, ProfileSettings } from '../models/profile-settings.model';
import { ProfileSelectionComponentInfo } from '../profile-selection/profile-selection-component-info';
import { ProfileInfo } from './../models/profile-info.model';
import { AuthConfigService } from './auth-config.service';
import { ProfileSettingsService } from './profile-settings.service';
import { WebApiServiceWithAuthConfig } from './webapi-with-auth-config.service';

@Injectable({
  providedIn: 'root',
})
export class ProfileService extends WebApiServiceWithAuthConfig {
  private profilesForCurrentUser: ProfileInfo[] = [];

  constructor(
    httpClient: HttpClient,
    abcConfigService: AbcConfigService,
    authConfigService: AuthConfigService,
    private profileSettingsService: ProfileSettingsService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    super(httpClient, abcConfigService, authConfigService);
  }

  private get API_URL_GetCurrentProfile(): string {
    return this.abcConfigService.configuration.WebApi.ServiceURLs.Account_ReadCurrentProfile;
  }

  private get API_URL_GetProfiles(): string {
    return this.abcConfigService.configuration.WebApi.ServiceURLs.Account_ReadProfiles;
  }

  private get API_URL_SaveCurrentProfile(): string {
    return this.abcConfigService.configuration.WebApi.ServiceURLs.Account_SaveCurrentProfile;
  }

  private get API_URL_SaveProfileMapImagePreview(): string {
    return this.abcConfigService.configuration.WebApi.ServiceURLs.Account_SaveProfileMapImagePreview;
  }

  private get appIdentifier(): string {
    return this.abcConfigService.configuration.ApplicationIdentifier;
  }

  public clearProfilesForCurrentUser(): void {
    this.profilesForCurrentUser = [];
  }

  public getProfilesForCurrentUser(refreshContext?: boolean): Observable<ProfileInfo[]> {
    const query = { OutputMode: 'Json', AppIdentifiers: [] };
    if (this.appIdentifier) {
      query.AppIdentifiers.push(this.appIdentifier);
    }
    return iif(
      () => this.profilesForCurrentUser.length > 0 && !refreshContext,
      of(this.profilesForCurrentUser),
      this.executeWebApiPOST(this.API_URL_GetProfiles, query).pipe(
        concatMap((response) => {
          if (response && Array.isArray(response) && response.length > 0) {
            this.clearProfilesForCurrentUser();
            return this.profileSettingsService.getCurrentProfileSettings().pipe(
              map((profileSettings) =>
                response.map((entry) => {
                  const pi = new ProfileInfo(
                    <string>entry['Id'],
                    <string>entry['DisplayText'],
                    <string>entry['Id'] === profileSettings?.id,
                    null,
                    <string>entry['MapImagePreview'],
                    <string>entry['Description']
                  );
                  this.profilesForCurrentUser.push(pi);
                  return pi;
                })
              )
            );
          } else {
            return of(<ProfileInfo[]>[]);
          }
        })
      )
    );
  }

  public getCurrentProfile(withSettings: boolean = false): Observable<ProfileInfo> {
    return this.profileSettingsService.getCurrentProfileSettings().pipe(
      concatMap((profileSettings) =>
        this.queryCurrentProfile(profileSettings, withSettings).pipe(
          concatMap((profileInfo) => {
            if (profileInfo && (!profileSettings || profileInfo.displayText !== profileSettings.displayText)) {
              // Update server profile on client
              return this.profileSettingsService
                .updateCurrentProfileSettings(new ProfileSettings(profileInfo))
                .pipe(map(() => profileInfo));
            } else {
              return of(profileInfo);
            }
          })
        )
      )
    );
  }

  private queryCurrentProfile(profileSettings: IProfileSettings, withSettings: boolean): Observable<ProfileInfo> {
    return this.executeWebApiPOST(this.API_URL_GetCurrentProfile, { OutputMode: 'Json' }).pipe(
      // TODO @todo Remove catchError as soon as ReadCurrentProfile is fixed in WebAPI
      catchError((err) => {
        // Swallow error as WebApi might bug out
        if (profileSettings) {
          // Client profile id is invalid
          profileSettings = null; //Settings are already removed from storage by Interceptor when this error was thrown there
        } // else: Invalid server profile - can be ignored.

        return of(<ArrayBuffer>null);
      }),
      map((response) => {
        const queriedProfileId: string = response ? <string>response['Id'] : null;
        const currentProfileId: string = profileSettings ? profileSettings.id : null;
        const settings = response ? <any>response['Settings'] : null;
        if (this.appIdentifier && settings?.Apps?.length) {
          const apps: { identifier: string }[] = settings.Apps;
          if (apps.findIndex((_) => _.identifier === this.appIdentifier) < 0) {
            this.navigateToProfileSelectionPage();
            return null;
          }
        }

        if (currentProfileId && (!queriedProfileId || currentProfileId !== queriedProfileId)) {
          return profileSettings.toProfileInfo(true, null);
        } else if (queriedProfileId && (!currentProfileId || currentProfileId === queriedProfileId)) {
          return new ProfileInfo(
            queriedProfileId,
            <string>response['DisplayText'],
            true,
            withSettings ? settings : null,
            <string>response['MapImagePreview'],
            <string>response['Description']
          );
        } else {
          return null;
        }
      })
    );
  }

  public setCurrentProfile(profileInfo: ProfileInfo): Observable<void> {
    return from(this.setCurrentProfileCore(profileInfo));
  }

  private async setCurrentProfileCore(profileInfo: ProfileInfo): Promise<void> {
    try {
      const cfg = await this.authConfigService.getCurrent(true).toPromise();

      if (cfg?.needsAuthentication || cfg?.useLocalSecurityContext || cfg?.useRemoteSecurityContext) {
        await this.executeWebApiPOST(this.API_URL_SaveCurrentProfile, {
          Id: profileInfo ? profileInfo.id : null,
        }).toPromise();
      }
    } catch (err) {}

    await this.profileSettingsService
      .setCurrentProfileSettings(profileInfo ? new ProfileSettings(profileInfo) : null)
      .toPromise();
  }

  public saveProfileMapImagePreview(profileId: string, mapimagePreview: string): Observable<void> {
    return this.executeWebApiPOST(this.API_URL_SaveProfileMapImagePreview, {
      Id: profileId,
      MapImagePreview: mapimagePreview,
    }).pipe(map((_) => {}));
  }

  public get hasCurrentProfile(): Observable<boolean> {
    return this.profileSettingsService.hasCurrentProfileSettings;
  }

  public getCurrentProfileSettings(): Observable<IProfileSettings> {
    return this.profileSettingsService.getCurrentProfileSettings();
  }

  public removeCurrentProfile(): Observable<void> {
    return this.setCurrentProfile(null);
  }

  public areProfilesSupported(router?: Router): boolean {
    return ProfileSelectionComponentInfo.areProfilesSupported(router ?? this.router);
  }

  public createProfileSelectionPageNavigationInfo(
    router?: Router,
    route?: ActivatedRoute,
    forwardTo?: string
  ): NavigationInfo {
    return ProfileSelectionComponentInfo.createProfileSelectionPageNavigationInfo(
      router ?? this.router,
      route ?? this.route,
      forwardTo
    );
  }

  public navigateToProfileSelectionPage(router?: Router, route?: ActivatedRoute, forwardTo?: string): Promise<boolean> {
    return ProfileSelectionComponentInfo.navigateToProfileSelectionPage(
      router ?? this.router,
      route ?? this.route,
      forwardTo
    );
  }

  public createProfileSelectionPageUrlTree(router?: Router, route?: ActivatedRoute, forwardTo?: string): UrlTree {
    return ProfileSelectionComponentInfo.createProfileSelectionPageUrlTree(
      router ?? this.router,
      route ?? this.route,
      forwardTo
    );
  }

  public async getAvailableObjectSpaces(): Promise<{ name: string; displayText: string }[]> {
    if ((await this.authConfigService.current.toPromise())?.systemName === 'BaSYS') {
      return [
        { displayText: 'All specialist fields', name: 'BaSYS.Complete' },
        { displayText: 'Asset Valuation', name: 'BaSYS.Commmon.UniWert' },
        { displayText: 'Cable', name: 'BaSYS.Energy' },
        { displayText: 'Gas', name: 'BaSYS.Gas' },
        { displayText: 'Indirect discharger', name: 'BaSYS.IndirectDischarger' },
        { displayText: 'Operation & maintenance', name: 'BaSYS.Commmon.Maintenance' },
        { displayText: 'Wastwater', name: 'BaSYS.Wastwater' },
        { displayText: 'Water', name: 'BaSYS.Water' },
        { displayText: 'User-defined', name: 'BaSYS.Custom' },
        { displayText: 'Master database', name: 'BaSYS.Master' },
      ];
    } else {
      return [
        { displayText: 'Alle Fachbereiche', name: 'ABC.Applications.KAOS.Data' },
        { displayText: 'Benutzerdefiniert', name: 'KAOS.Custom' },
        { displayText: 'Masterdatenbank', name: 'KAOS.Master' },
      ];
    }
  }
}
